// ABOUTME: Public page component for displaying shared job roles
// ABOUTME: Shows role details without authentication for public sharing

import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Building2, Calendar, DollarSign, Briefcase, Users, ArrowLeft } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';

interface PublicRole {
  id: string;
  title: string;
  department: string;
  location: string;
  salary_range?: string;
  description?: string;
  requirements?: string;
  bonus_amount?: string;
  currency?: string;
  deadline?: string;
  Company?: string;
  required_tools?: string[];
  Essential_Skills?: string;
  Key_deliverables?: string;
  experience?: string;
}

const PublicRolePage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: roleData, isLoading, error } = useQuery({
    queryKey: ['public-role', slug],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-public-role', {
        body: { slug }
      });

      if (error) throw error;
      return data as { role: PublicRole };
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-6">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-12 w-3/4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !roleData?.role) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Role Not Found</CardTitle>
            <CardDescription>
              This role link may be invalid or the role is no longer active.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild>
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { role } = roleData;

  const formatSalary = (range?: string, currency = 'USD') => {
    if (!range) return 'Competitive';
    return `${range} ${currency}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Link>
              </Button>
            </div>
            
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-foreground">{role.title}</h1>
              
              <div className="flex flex-wrap gap-4 text-muted-foreground">
                {role.Company && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>{role.Company}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span>{role.department}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{role.location}</span>
                </div>
                
                {role.deadline && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Deadline: {new Date(role.deadline).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Key Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Salary Range
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold">{formatSalary(role.salary_range, role.currency)}</p>
              </CardContent>
            </Card>

            {role.bonus_amount && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Referral Bonus
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-semibold">{role.bonus_amount}</p>
                </CardContent>
              </Card>
            )}

            {role.experience && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Experience Required</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{role.experience}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {role.description && (
                <Card>
                  <CardHeader>
                    <CardTitle>Job Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none">
                      <p className="whitespace-pre-wrap">{role.description}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {role.Key_deliverables && (
                <Card>
                  <CardHeader>
                    <CardTitle>Key Deliverables</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none">
                      <p className="whitespace-pre-wrap">{role.Key_deliverables}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {role.requirements && (
                <Card>
                  <CardHeader>
                    <CardTitle>Requirements</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none">
                      <p className="whitespace-pre-wrap">{role.requirements}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {role.Essential_Skills && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Essential Skills</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none">
                      <p className="whitespace-pre-wrap text-sm">{role.Essential_Skills}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {role.required_tools && role.required_tools.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Required Tools</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {role.required_tools.map((tool, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tool}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Call to Action */}
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-lg">Interested in Referring?</CardTitle>
                  <CardDescription>
                    Know someone perfect for this role? Get in touch to make a referral.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full" asChild>
                    <Link to="/">
                      Make a Referral
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicRolePage;